import React from 'react';
import { motion } from 'framer-motion';
import { IoCloudDownload, IoCheckmark, IoAlert } from 'react-icons/io5';

const DataLoader = ({ 
  isLoading, 
  progress = 0, 
  error = null, 
  title = "Loading Data", 
  subtitle = "Please wait while we load your data...",
  size = "large" // small, medium, large
}) => {
  if (!isLoading && !error) return null;

  const sizeClasses = {
    small: {
      container: "p-4",
      icon: "w-8 h-8",
      title: "text-lg",
      subtitle: "text-sm",
      progress: "h-2",
      percentage: "text-sm"
    },
    medium: {
      container: "p-6",
      icon: "w-12 h-12",
      title: "text-xl",
      subtitle: "text-base",
      progress: "h-3",
      percentage: "text-base"
    },
    large: {
      container: "p-8",
      icon: "w-16 h-16",
      title: "text-2xl",
      subtitle: "text-lg",
      progress: "h-4",
      percentage: "text-lg"
    }
  };

  const classes = sizeClasses[size];

  const getLoadingStage = () => {
    if (progress < 70) return "Downloading";
    if (progress < 80) return "Decompressing";
    if (progress < 95) return "Processing";
    return "Finalizing";
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 ${classes.container}`}
        >
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <IoAlert className={`${classes.icon} text-red-600`} />
              </div>
            </div>
            
            <h3 className={`font-bold text-gray-800 mb-2 ${classes.title}`}>
              Loading Failed
            </h3>
            
            <p className={`text-gray-600 mb-6 ${classes.subtitle}`}>
              {error}
            </p>
            
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 ${classes.container}`}
      >
        <div className="text-center">
          {/* Loading Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="p-4 bg-blue-100 rounded-full">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <IoCloudDownload className={`${classes.icon} text-blue-600`} />
                </motion.div>
              </div>
              
              {/* Pulse animation */}
              <motion.div
                className="absolute inset-0 bg-blue-400 rounded-full opacity-20"
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </div>
          
          {/* Title and Subtitle */}
          <h3 className={`font-bold text-gray-800 mb-2 ${classes.title}`}>
            {title}
          </h3>
          
          <p className={`text-gray-600 mb-6 ${classes.subtitle}`}>
            {subtitle}
          </p>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className={`w-full bg-gray-200 rounded-full ${classes.progress} overflow-hidden`}>
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
          </div>
          
          {/* Progress Details */}
          <div className="flex justify-between items-center text-gray-600">
            <span className={`font-medium ${classes.percentage}`}>
              {getLoadingStage()}...
            </span>
            <span className={`font-bold text-blue-600 ${classes.percentage}`}>
              {Math.round(progress)}%
            </span>
          </div>
          
          {/* File Size Info */}
          {/* <div className="mt-4 text-xs text-gray-500">
            <p>📦 Compressed: 1.3MB • 🚀 90% smaller than original</p>
          </div> */}
        </div>
      </motion.div>
    </div>
  );
};

export default DataLoader; 